/* Compare Sizes — shared selection store.
   Single source of truth for the compare-sizes flow, persisted in
   localStorage under `pwt_csz_v1`. Loaded by the PDP drawer
   (compare-drawer.js) and the fallback pages (compare-select.js /
   compare-sizes.js). Items are one-per-product-handle, capped at 4;
   legacy per-size state is migrated on read.
   Falls back to in-memory state when storage is unavailable
   (private mode); pages additionally hand state off via ?items=
   on the two navigation buttons for that case. */
(function () {
  'use strict';

  var KEY = 'pwt_csz_v1';
  var MAX_ITEMS = 4;

  var memoryState = null;
  var listeners = [];

  function emptyState() {
    return { v: 1, updatedAt: 0, base: { handle: null }, items: [] };
  }

  /* One item per product handle (the per-size selection concept is gone) and
     never more than MAX_ITEMS. Runs on read AND write so state saved by the
     old flow (up to 20 per-size entries) migrates the first time it's touched. */
  function normalize(state) {
    var seen = {};
    var items = [];
    state.items.forEach(function (it) {
      if (!it || !it.handle || seen[it.handle]) return;
      seen[it.handle] = true;
      items.push(it);
    });
    state.items = items.slice(0, MAX_ITEMS);
    return state;
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return memoryState || emptyState();
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.items)) return emptyState();
      return normalize(parsed);
    } catch (e) {
      return memoryState || emptyState();
    }
  }

  function write(state) {
    normalize(state);
    state.updatedAt = Date.now();
    memoryState = state;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode / quota — keep in-memory copy only */
    }
    notify();
  }

  function notify() {
    var state = read();
    listeners.forEach(function (fn) {
      try { fn(state); } catch (e) { /* listener errors must not break the store */ }
    });
  }

  function keyFor(item) {
    return [item.handle || '', item.colorHandle || '', (item.size || '').toLowerCase()].join('|');
  }

  var CompareStore = {
    MAX_ITEMS: MAX_ITEMS,
    keyFor: keyFor,

    get: function () { return read(); },

    count: function () { return read().items.length; },

    setBase: function (handle) {
      var state = read();
      if (state.base.handle === handle) return;
      state.base = { handle: handle };
      write(state);
    },

    has: function (item) {
      var k = keyFor(item);
      return read().items.some(function (it) { return keyFor(it) === k; });
    },

    itemsFor: function (handle, colorHandle) {
      return read().items.filter(function (it) {
        return it.handle === handle && (colorHandle == null || it.colorHandle === colorHandle);
      });
    },

    hasProduct: function (handle) {
      return read().items.some(function (it) { return it.handle === handle; });
    },

    /* Drawer-era add: one entry per product. Replaces any existing entry for
       the same handle (keeping its position/addedAt) instead of stacking sizes.
       item: { handle, colorHandle, color, size, variantId, title } */
    addProduct: function (item) {
      var state = read();
      var replaced = false;
      state.items = state.items.map(function (it) {
        if (it.handle !== item.handle) return it;
        replaced = true;
        item.addedAt = it.addedAt;
        return item;
      });
      if (!replaced) {
        if (state.items.length >= MAX_ITEMS) return { ok: false, reason: 'cap' };
        item.addedAt = Date.now();
        state.items.push(item);
      }
      write(state);
      return { ok: true };
    },

    removeProduct: function (handle) {
      var state = read();
      var before = state.items.length;
      state.items = state.items.filter(function (it) { return it.handle !== handle; });
      if (state.items.length !== before) write(state);
    },

    /* item: { handle, colorHandle, color, size, variantId, title } */
    addItem: function (item) {
      var state = read();
      var k = keyFor(item);
      var exists = state.items.some(function (it) { return keyFor(it) === k; });
      if (exists) return { ok: true, reason: 'duplicate' };
      if (state.items.length >= MAX_ITEMS) return { ok: false, reason: 'cap' };
      item.addedAt = Date.now();
      state.items.push(item);
      write(state);
      return { ok: true };
    },

    removeItem: function (key) {
      var state = read();
      var before = state.items.length;
      state.items = state.items.filter(function (it) { return keyFor(it) !== key; });
      if (state.items.length !== before) write(state);
    },

    /* Replace all selections for one (handle, colorHandle) with the given sizes.
       sizes: [{ size, variantId }] — returns { ok, reason } (cap-aware).
       addedAt is preserved for sizes that were already selected so re-applying
       doesn't reorder the compare table. */
    applySizes: function (base, sizes) {
      var state = read();
      var previous = {};
      var kept = state.items.filter(function (it) {
        if (it.handle === base.handle && it.colorHandle === base.colorHandle) {
          previous[keyFor(it)] = it.addedAt;
          return false;
        }
        return true;
      });
      var additions = sizes.map(function (s) {
        var item = {
          handle: base.handle,
          colorHandle: base.colorHandle,
          color: base.color,
          title: base.title,
          size: s.size,
          variantId: s.variantId
        };
        item.addedAt = previous[keyFor(item)] || Date.now();
        return item;
      });
      if (kept.length + additions.length > MAX_ITEMS) return { ok: false, reason: 'cap' };
      state.items = kept.concat(additions);
      write(state);
      return { ok: true };
    },

    clearItems: function () {
      var state = read();
      state.items = [];
      write(state);
    },

    /* Serialize items for URL handoff when storage is unavailable. */
    toParam: function () {
      return read().items.map(function (it) {
        return [it.handle, it.colorHandle || '', it.size].join(':');
      }).join(',');
    },

    /* Hydrate from a ?items= param (only fills gaps — storage wins). */
    hydrateFromParam: function (param) {
      if (!param) return;
      var state = read();
      if (state.items.length) return;
      param.split(',').forEach(function (chunk) {
        var bits = chunk.split(':');
        if (bits.length < 3 || !bits[0] || !bits[2]) return;
        if (state.items.length >= MAX_ITEMS) return;
        state.items.push({
          handle: bits[0],
          colorHandle: bits[1] || null,
          color: null,
          title: null,
          size: bits[2],
          variantId: null,
          addedAt: Date.now()
        });
      });
      write(state);
    },

    onChange: function (fn) { listeners.push(fn); }
  };

  /* Cross-tab sync: another tab writing the key re-renders this one. */
  try {
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) notify();
    });
  } catch (e) { /* no-op */ }

  window.CompareStore = CompareStore;
})();
